import React, { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";

import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Input,
  Button,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer";
import { listAllSubscription } from "./_requests";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useProfile } from "../../Components/Hooks/UserHooks";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { formatDate } from "../../helpers/FunctionHelpers";

const Subscription = () => {
  const { userProfile } = useProfile();
  const [subscription, setSubscription] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  console.log("filteredData", filteredData)
  type DropdownOptions = {
    plans: string[];
    users: string[];
    products: string[];
    types: string[];
  };

  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptions>({
    plans: [],
    users: [],
    products: [],
    types: [],
  });

  const [filters, setFilters] = useState({
    is_active: "",
    plan: "",
    name: "",
    product: "",
    type: "",
    start_date: "",
    end_date: "",
  });

  const fetchSubscription = async () => {
    try {
      const res = await listAllSubscription();
      const data = res.data || [];
      setSubscription(data);
      setFilteredData(data);

      // Extract unique dropdown options
      const plans = Array.from(new Set<string>(data.map((d: any) => d.subscription?.name).filter(Boolean)));

      const users = Array.from(new Set<string>(data.map((d: any) => d.user?.name).filter(Boolean)));
      const products = Array.from(new Set<string>(data.flatMap((d: any) =>
        d.details?.map((item: any) => item.product?.name).filter(Boolean) || []
      )));
      const types = Array.from(new Set<string>(data.map((d: any) => d.type_name).filter(Boolean)));

      setDropdownOptions({ plans, users, products, types });

    } catch (err) {
      toast.error("Failed to fetch subscriptions");
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const applyFilters = () => {
    let filtered = [...subscription];

    if (filters.is_active !== "") {
      filtered = filtered?.filter((d) => String(d.is_active) === filters.is_active);
    }
    if (filters.plan) {
      filtered = filtered?.filter((d) => d.subscription?.name === filters.plan);
    }
    if (filters.name) {
      filtered = filtered?.filter((d) => d.user?.name === filters.name);
    }
    if (filters.product) {
      filtered = filtered?.filter((d) =>
        d.details?.some((item: any) => item.product?.name === filters.product)
      );
    }
    if (filters.type) {
      filtered = filtered?.filter((d) => d.type_name === filters.type);
    }

    // Date filter
    if (filters.start_date || filters.end_date) {
      const startDate = filters.start_date ? new Date(filters.start_date) : null;
      const endDate = filters.end_date ? new Date(filters.end_date) : null;

      if (startDate) {
        startDate.setHours(0, 0, 0, 0); // Start of day
      }
      if (endDate) {
        endDate.setHours(23, 59, 59, 999); // End of day
      }

      filtered = filtered.filter((d) => {
        const subscriptionDate = d.start_date ? new Date(d.start_date) : null;
        if (!subscriptionDate) return false;

        let matchesStart = true;
        let matchesEnd = true;

        if (startDate) {
          matchesStart = subscriptionDate >= startDate;
        }
        if (endDate) {
          matchesEnd = subscriptionDate <= endDate;
        }

        return matchesStart && matchesEnd;
      });
    }

    setFilteredData(filtered);
  };

  const clearFilters = () => {
    setFilters({
      is_active: "",
      plan: "",
      name: "",
      product: "",
      type: "",
      start_date: "",
      end_date: "",
    });
    setFilteredData(subscription);
  };


  const exportToExcel = async () => {
    if (filteredData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Subscriptions");

      // Define headers with order
      const headers = [
        "S.No",
        "Order ID",
        "User Name",
        "User Mobile",
        "Plan Name",
        "Type",
        "Route",
        "Slot",
        "Products",
        "Variants",
        "Validity",
        "Amount (₹)",
        "Subscribed Date",
        "Start Date",
        "End Date",
        "No. of Deliveries",
        "Remaining Deliveries",
        "Address",
        // "Payment Status",
        "Active Status",
      ];

      // Add headers
      worksheet.addRow(headers);

      // Style headers (bold, color, center, freeze row)
      worksheet.getRow(1).eachCell((cell: any) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // White text
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" }, // Blue background
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      // Freeze header row
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      // Map data
      filteredData.forEach((item, index) => {
        const row = worksheet.addRow([
          index + 1,
          item.user_subscription_order_id || "-",
          item.user?.name || "-",
          item.user?.mobile_number || "-",
          item.subscription?.name || "-",
          item.type_name || "-",
          item.route?.name || "-",
          item.slot_type_name || "-",
          item.details?.map((d: any) => d.product?.name || "-").join("; ") || "-",
          item.details?.map((d: any) => `${d.variant_value || "-"} ${d.variant?.name || "-"}`).join("; ") || "-",
          item.details?.map((d: any) => `${d.validity || "-"} ${d.validity_type || "-"}`).join("; ") || "-",
          item.gross_amount ? `₹${item.gross_amount.toLocaleString("en-IN")}` : "-",
          formatDate(item.created_at),
          formatDate(item.start_date),
          formatDate(item.end_date),
          item.delivery_count || "-",
          item.remaining_days_plan || "-",
          item.shipping_address || "-",
          // item.payment_status || "-",
          item.is_active === 1 ? "Active" : "Expired",
        ]);

        // Bold User Name & Route
        row.getCell(3).font = { bold: true };
        row.getCell(7).font = { bold: true };
      });


      // Auto-fit column widths
      (worksheet.columns as Partial<ExcelJS.Column>[]).forEach((col) => {
        let maxLength = 0;
        if (col.eachCell) {
          col.eachCell({ includeEmpty: false }, (cell: ExcelJS.Cell) => {
            const cellLength = cell.value ? cell.value.toString().length : 0;
            if (cellLength > maxLength) maxLength = cellLength;
          });
        }
        col.width = maxLength + 2; // just enough, not too wide
      });



      // Save file
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `subscriptions_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Failed to export Excel");
    }
  };

  const applyTodayFilter = () => {
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];

    setFilters({
      ...filters,
      start_date: todayString,
      end_date: todayString
    });

    // Apply the filter immediately
    let filtered = [...subscription];
    filtered = filtered.filter((d) => {
      if (!d.start_date) return false;
      const subscriptionDate = new Date(d.start_date).toISOString().split('T')[0];
      return subscriptionDate === todayString;
    });

    setFilteredData(filtered);
  };

  const columns = useMemo(() => [
    {
      header: "S.No",
      id: "sno",
      enableColumnFilter: false,
      cell: (cellProps: any) => {
        const { pageIndex, pageSize } = cellProps.table.getState().pagination;
        return pageIndex * pageSize + cellProps.row.index + 1;
      },
    },
    {
      header: "User",
      accessorKey: "user.name",
      cell: ({ row }: any) => {
        const user = row.original.user;
        const createdAt = row.original.created_at;
        
        if (!user) return "-";

        // Native JS comparison for reliability (sub within last 10 days)
        const subDate = createdAt ? new Date(createdAt) : null;
        const isNew = subDate && (new Date().getTime() - subDate.getTime()) <= (7 * 24 * 60 * 60 * 1000);

        return (
          <div style={{ minWidth: "150px" }}>
            <Link to={`/user/view/${user.id}`} className="custom-redirect-link fw-bold">
              {user.name}
            </Link>
            {isNew && (
              <div className="mt-1">
                <span className="badge bg-success-subtle text-success p-1 px-2" style={{ fontSize: '11px', border: "1px solid #0ab39c66" }}>
                  <i className="ri-user-star-line align-middle me-1"></i> New Customer
                </span>
              </div>
            )}
            <p className="mt-1 mb-0 text-muted small">{user.mobile_number}</p>
          </div>
        );
      },
    },

    {
      header: "Plan",
      accessorKey: "subscription.name",
      cell: ({ row }: any) => {
        const plan = row.original.subscription;
        const type = row.original.type_name || "-";
        const slot = row.original.slot_type_name || "-";
        const orderId = row.original.user_subscription_order_id || "-";
        const subscribed = row.original.created_at;
        const subUser = row.original.user;
        const isFirstSub = row.original.is_first_subscription || subUser?.is_trial_used === 0 || subUser?.is_trial_used === false;
        return plan ? (
          <div>
            <div>
              <Link to={`/view-user-subscription/${row.original.id}`} className="custom-redirect-link">
                {plan.name}
              </Link>
              <span className="text-muted"> [{slot || "-"}]</span>
              {isFirstSub && <span className="badge bg-danger text-white ms-1">NEW</span>}
            </div><br />
            <div className="fw-bold text-primary" style={{ fontSize: "1.0em" }}>
              {type} - {orderId}
            </div><br />
            <div>
              <strong>Subscribed:</strong> {formatDate(subscribed || "-")}
            </div>
          </div>
        ) : "-";
      },
    },

    {
      header: "Subscription Period",
      id: "subscription_period",
      cell: ({ row }: any) => {
        const start = row.original.start_date;
        const end = row.original.end_date;


        return (
          <div>

            {/* Period */}
            <span className="font-medium">
              {/* Start DateTime */}
              {formatDate(start)} -
              <br />
              {/* End DateTime */}
              {formatDate(end)}  </span>
          </div>
        );
      },
    },

    {
      header: "Order Summary",
      id: "order_summary",
      cell: ({ row }: any) => {
        const details = row.original.details || [];
        const grossAmount = row.original.gross_amount;
        const status = row.original.payment_status;
        const remainingDays = row.original.remaining_days_plan;
        const deliveryCount = row.original.delivery_count;

        return (
          <div>
            {/* Products List */}
            {details.length ? (
              <div>
                {details.map((item: any, idx: number) => (
                  <div key={idx}>
                    <strong>{item.product?.name || "-"}</strong> -{" "}
                    {item.variant?.name || "-"} ({item.variant_value || "-"})
                  </div>
                ))}
              </div>
            ) : (
              "-"
            )}

            {/* Divider line */}
            <hr style={{ margin: "4px 0" }} />

            {/* Payment Details */}
            <div>
              <div>
                <strong>Amt:</strong>{" "}
                {grossAmount
                  ? new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: "INR",
                  }).format(grossAmount)
                  : "-"}
              </div>
              <div>
                <strong>Status:</strong>{" "}
                <span style={{ color: status === "Paid" ? "green" : "red" }}>
                  {status || "-"}
                </span>
              </div>
              <div>
                <strong>Total Deliveries:</strong> {deliveryCount || "-"} Days
              </div>
              <div>
                <strong>Remaining:</strong> {remainingDays || "-"} Days
              </div>
            </div>
          </div>
        );
      },
    },

    {
      header: "Shipping Address",
      accessorKey: "shipping_address",
      cell: ({ row }: any) => {
        const address = row.original.shipping_address;
        return (
          <div>
            {/* Address */}
            <div>{address || "-"}</div>
          </div>
        );
      },
    },

    {
      header: "Receipt",
      accessorKey: "receipt",
      cell: ({ getValue }: any) => {
        const text = getValue();
        return text ? (
          <a href={text} target="_blank" rel="noopener noreferrer">
            View Receipt
          </a>
        ) : "-";
      },
    },
    {
      header: "Active Status",
      accessorKey: "is_active",
      cell: ({ row }: any) => {
        const isActive = row.original.is_active;
        return isActive === 1 ? (
          <span className="badge bg-success">Active</span>
        ) : isActive === 0 ? (
          <span className="badge bg-danger">Expired</span>
        ) : (
          <span className="badge bg-secondary">-</span>
        );
      },
    },
  ], []);

  document.title = "Paal Dappa | Admin panel";

  return (
    <React.Fragment>
      <div className="page-content">
        <ToastContainer closeButton={false} limit={1} />
        <Container fluid>
          <BreadCrumb title="Subscription" pageTitle="Dashboard" />
          <Row>
            <Col lg={12}>
              <Card id="SubscriptionList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <Col sm={12}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-3">Subscription List</h5>
                        <div>
                          <Link to="/subscription/add" className="btn btn-success me-3">
                            <i className="ri-add-line align-bottom me-1" /> Add Subscription
                          </Link>
                          <button
                            type="button"
                            className="btn btn-info me-3"
                            onClick={exportToExcel}
                          >
                            <i className="ri-file-download-line align-bottom me-1" /> Download Excel
                          </button>

                        </div>
                      </div>
                      <Form className="row g-3 align-items-end">
                        <Col md={2}>
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            name="start_date"
                            value={filters.start_date}
                            onChange={handleFilterChange}
                          />
                        </Col>
                        <Col md={2}>
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            name="end_date"
                            value={filters.end_date}
                            onChange={handleFilterChange}
                          />
                        </Col>
                        <Col md={2}>
                          <Button color="outline-secondary" onClick={applyTodayFilter} style={{ marginTop: '30px' }}>
                            Today
                          </Button>
                        </Col>
                        <Col md={2}>
                          <Label>Plan</Label>
                          <Input type="select" name="plan" value={filters.plan} onChange={handleFilterChange}>
                            <option value="">All</option>
                            {dropdownOptions.plans.map((plan, idx) => (
                              <option key={idx} value={plan}>{plan}</option>
                            ))}
                          </Input>
                        </Col>
                        <Col md={2}>
                          <Label>User Name</Label>
                          <Input type="select" name="name" value={filters.name} onChange={handleFilterChange}>
                            <option value="">All</option>
                            {dropdownOptions.users.map((user, idx) => (
                              <option key={idx} value={user}>{user}</option>
                            ))}
                          </Input>
                        </Col>
                        <Col md={2}>
                          <Label>Product</Label>
                          <Input type="select" name="product" value={filters.product} onChange={handleFilterChange}>
                            <option value="">All</option>
                            {dropdownOptions.products.map((prod, idx) => (
                              <option key={idx} value={prod}>{prod}</option>
                            ))}
                          </Input>
                        </Col>
                        <Col md={2}>
                          <Label>Type</Label>
                          <Input type="select" name="type" value={filters.type} onChange={handleFilterChange}>
                            <option value="">All</option>
                            {dropdownOptions.types.map((type, idx) => (
                              <option key={idx} value={type}>{type}</option>
                            ))}
                          </Input>
                        </Col>
                        <Col md={3}>
                          <Label className="d-block">Active Status</Label>
                          <div className="d-flex gap-2">
                            <Button
                              color={filters.is_active === "" ? "primary" : "outline-primary"}
                              size="sm"
                              onClick={() => setFilters({ ...filters, is_active: "" })}
                            >
                              All
                            </Button>
                            <Button
                              color={filters.is_active === "1" ? "success" : "outline-success"}
                              size="sm"
                              onClick={() => setFilters({ ...filters, is_active: "1" })}
                            >
                              Active
                            </Button>
                            <Button
                              color={filters.is_active === "0" ? "danger" : "outline-danger"}
                              size="sm"
                              onClick={() => setFilters({ ...filters, is_active: "0" })}
                            >
                              Expired
                            </Button>
                          </div>
                        </Col>
                        <Col md={2} style={{ marginTop: '30px' }}>
                          <Button color="primary" onClick={applyFilters}>Apply</Button>{" "}
                          <Button color="secondary" onClick={clearFilters}>Clear</Button>
                        </Col>
                      </Form>
                    </Col>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  <div>
                    <TableContainer
                      columns={columns}
                      data={filteredData || []}
                      isGlobalFilter={true}
                      customPageSize={10}
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-top table-bordered align-top table-nowrap mb-0"
                      SearchPlaceholder="Search Subscription..."
                    />
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Subscription;